// covered-by: frontends/carrie/src/components/memory-citation/__tests__/MemoryCitationChip.test.tsx

import { ArrowRight } from "@phosphor-icons/react";
import { Link as RouterLink } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { MemoryChipStatic } from "./MemoryChipFace";
import { memoryMeta } from "./memoryLabels";
import { MEMORY_LINK_TO, memoryTabSearch } from "./memoryLink";

import { type LinkableMemory } from "../../utils/memoryCitationFields";

// why: the width cap is Radix's available width, not the viewport, so a boundary narrower than 360px (a 380px comment panel leaves 348) narrows the card instead of leaving it 4px from the window edge; 100vw is the fallback before Radix has measured.
/**
 * The whole saved memory a chip stands for, with where it came from.
 *
 * The cap-and-scroll is the shared popover standard
 * (`frontend-design-standards`): a long memory must not push the card off the
 * screen, so the panel caps its height and the text scrolls inside it.
 */
export const MEMORY_POPOVER_CLASS =
  "max-h-[min(70vh,420px)] w-[360px] max-w-[min(360px,var(--radix-hover-card-content-available-width,100vw))] overflow-y-auto rounded-2xl border border-puntt-neutral-gray-6 bg-base-white p-4 text-left shadow-lg";

export function MemoryCitationPopover({
  memory,
  testIdSuffix,
}: {
  memory: LinkableMemory;
  testIdSuffix: string;
}) {
  const { t, i18n } = useTranslation("ui");
  const meta = memoryMeta(memory, t, i18n.language);
  const learned = [meta.origin, meta.date].filter(Boolean).join(" · ");
  const prefix = `memory-citation-popover-${testIdSuffix}`;

  return (
    <div
      role="group"
      aria-label={t("askPuntt.memoryCitation.popoverAria", {
        n: memory.number,
      })}
      data-testid={prefix}
      className={MEMORY_POPOVER_CLASS}
    >
      <div className="mb-2 flex items-center gap-2">
        <MemoryChipStatic number={memory.number} testId={`${prefix}-chip`} />
        {meta.type ? (
          <>
            <span
              data-testid={`${prefix}-type`}
              className="text-[13px] font-semibold text-puntt-neutral-gray-12"
            >
              {meta.type}
            </span>
            <span className="ml-auto text-[11px] text-puntt-neutral-gray-11">
              {t("askPuntt.memoryCitation.savedMemory")}
            </span>
          </>
        ) : (
          <span
            data-testid={`${prefix}-type`}
            className="text-[13px] font-semibold text-puntt-neutral-gray-12"
          >
            {t("askPuntt.memoryCitation.savedMemory")}
          </span>
        )}
      </div>
      <div
        data-testid={`${prefix}-text`}
        className="max-h-[200px] overflow-y-auto whitespace-pre-line text-[13px] leading-5 text-puntt-neutral-gray-12"
      >
        {memory.text}
      </div>
      <div className="mt-2.5 border-t border-puntt-neutral-gray-4 pt-2.5 text-[12px] leading-4 text-puntt-neutral-gray-11">
        {learned ? <p data-testid={`${prefix}-origin`}>{learned}</p> : null}
        <p
          data-testid={`${prefix}-note`}
          className="mt-0.5 text-puntt-neutral-gray-11"
        >
          {t("askPuntt.memoryCitation.savedNote")}
        </p>
      </div>
      <RouterLink
        to={MEMORY_LINK_TO}
        search={memoryTabSearch(memory.text)}
        data-testid={`${prefix}-open-in-memories`}
        className="mt-2.5 inline-flex items-center gap-1 text-[12px] font-semibold leading-4 text-[color:var(--puntt-link-memory)] no-underline hover:underline"
      >
        {t("askPuntt.memoryCitation.openInMemories")}
        <ArrowRight size={12} aria-hidden="true" />
      </RouterLink>
    </div>
  );
}
